import { DeliveryCard, DispatchCard, LoadingCard } from 'src/entities/dispatch';

import { isAssignedDeliveryCard } from './resolveTripSpan';
import { LongHaulTrip } from './types';

/**
 * そのバーに属するカードだけを取り出す。
 *
 * `trip.source` は回転（RotationRow）なので、積地が 2 つある回転では別の積地の
 * カードも混ざる。バーは積地ごとなので、バーの legRun を持つカードだけを見る
 * （ユーザー指摘 2026-09-29 でバーを積地ごとに分けた）。
 */
export function tripCardsOf(trip: LongHaulTrip): DispatchCard[] {
  const legRunIds = new Set(trip.legRunIds);
  return trip.source.cards.filter(
    (card) => (card.legRunIds ?? []).some((id) => legRunIds.has(id)),
  );
}

/**
 * バーを押したときに降地カードへ添える積地のカード。決まらなければ null。
 *
 * サンプル（MultiDayBoard.tsx:798-803 の `longHaulTrip`）は情報カードに積地と降地を
 * 並べて出す。こちらは地点ごとにカードが分かれているので、降地カードの相方をここで解く。
 *
 * 積地カードは placeKey（地点）ごとに作られる（`adapter.ts` の `buildLoadingCard`）ので、
 * 積地 1 つぶんのバーなら 1 枚に決まるのが正。2 枚あるのは積地キーの解決が崩れた状態で、
 * どちらを出すかを決められないので出さない（片方だけ出すと嘘になる）。
 */
export function resolveTripLoadingCard(trip: LongHaulTrip): LoadingCard | null {
  const loadingCards = tripCardsOf(trip).filter(
    (card): card is LoadingCard => card.type === 'loading',
  );
  return loadingCards.length === 1 ? loadingCards[0] : null;
}

/**
 * バーを押したとき、地点を選ばせずに情報ダイアログへ直行する降地カード。
 * 直行できないときは null（＝運行パネルを出して地点を選ばせる）。
 *
 * 長距離は 1 台 1 日 1 案件（積 1・降 1）が多く、毎回地点を選ばせるのは過剰
 * というユーザー判断による分岐（2026-09-24）。
 *
 * Why 降地カード: 未割当の詳細（UnassignedItem の storeName / geo）もカード型配車の
 * 詳細も降地を主に扱う。直行だけ積地を出すと、同じ案件を別の入口から見たときに
 * 出る地点が変わる。
 *
 * Why 降地カードの枚数も見る: 判定を `unloadingNames` だけに任せると、名前が同じで
 * 座標の違う降地（カードは 2 枚、名前は 1 つ）で開くカードが決まらない。開く先が
 * 1 枚に決まるときだけ直行する。
 *
 * Why legRunIds を見る: 直行した先の器は legRunIds を持つカードにだけ運行全体の操作
 * （未割り当てに戻す・他車両へ配車）を描く。空のカードへ直行すると、運行パネルを
 * 通らないぶんその 2 つへ到達できなくなる（DetailPanelShell の canMoveOrReset）。
 *
 * Why このバーの legRun で絞る: バーは積地ごとなので、`trip.source`（回転）のカードには
 * 別の積地ぶんも混ざる。回転全体で数えると、降地が 1 つしかないバーを押しても
 * 枚数が合わずに運行パネルへ回ってしまう。
 */
export function resolveDirectStopCard(trip: LongHaulTrip): DeliveryCard | null {
  // 降地が複数ある運行は、どこを見たいかが押した人にしか分からない
  if (trip.unloadingNames.length > 1) return null;
  const deliveryCards = tripCardsOf(trip).filter(isAssignedDeliveryCard);
  if (deliveryCards.length !== 1) return null;
  const [card] = deliveryCards;
  return (card.legRunIds?.length ?? 0) > 0 ? card : null;
}
